import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDriverAuth } from '../../context/DriverAuthContext';
import useWs from '../../../hooks/useWs';
import '../../DriverApp.css';
import SAR from '../../../components/SAR';

const Money = ({ n }) => n != null ? <>{parseFloat(n).toFixed(2)}{' '}<SAR className="h-[0.85em] w-auto" /></> : <>-</>;

const STEPS = [
  { key: 'accepted',           label: 'قبلت الطلب',          icon: '✓' },
  { key: 'en_route_pickup',    label: 'في الطريق للشيب',      icon: '🚗' },
  { key: 'at_source',          label: 'وصلت للشيب',           icon: '🚢' },
  { key: 'loading',            label: 'جاري التعبئة',         icon: '💧' },
  { key: 'en_route_delivery',  label: 'في الطريق للعميل',     icon: '📦' },
  { key: 'arrived',            label: 'وصلت للعميل',          icon: '📍' },
  { key: 'delivering',         label: 'جاري التسليم',         icon: '🤝' },
  { key: 'completed',          label: 'تم الإيصال ✓',         icon: '✅' },
];

const STEP_INDEX = Object.fromEntries(STEPS.map((s, i) => [s.key, i]));

export default function ActiveOrderPage() {
  const { apiCall } = useDriverAuth();
  const nav = useNavigate();

  const [order,    setOrder]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await apiCall('/orders/active');
      if (!d.active_order) { nav('/driver/home', { replace: true }); return; }
      setOrder(d.active_order);
    } catch { setError('تعذّر تحميل الطلب'); }
    finally   { setLoading(false); }
  }, [apiCall, nav]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 15000); // fallback only
    return () => clearInterval(pollRef.current);
  }, [load]);

  // WS: instant update when order status changes
  const token = localStorage.getItem('drv_token') || '';
  useWs(order?.id ? `/order/${order.id}` : null, load, token);

  const updateStatus = async () => {
    if (!order?.next_status) return;
    setUpdating(true); setError(''); setSuccess('');
    try {
      const res = await apiCall(`/orders/${order.id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: order.next_status }),
      });
      setSuccess(res.status_label || 'تم التحديث');
      if (res.status === 'completed') {
        setTimeout(() => nav('/driver/home', { replace: true }), 2000);
      } else {
        await load();
      }
    } catch (e) { setError(e.message || 'فشل التحديث'); }
    finally      { setUpdating(false); }
  };

  const openMap = (lat, lng) => {
    window.open(`https://maps.google.com/?q=${lat},${lng}`, '_blank');
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <div className="drv-spinner"/>
    </div>
  );

  if (!order) return (
    <div className="drv-empty">
      <h3>لا يوجد طلب نشط</h3>
      <Link to="/driver/home" className="drv-btn drv-btn--primary" style={{ display: 'flex', marginTop: 16 }}>
        العودة
      </Link>
    </div>
  );

  const currentIdx = STEP_INDEX[order.status] ?? 0;

  return (
    <div>
      {/* Order header */}
      <div className="drv-card">
        <div className="drv-order__header">
          <div>
            <div className="drv-order__num">#{order.order_number}</div>
            <div className="drv-order__status">{order.status_label || order.status}</div>
          </div>
          <Link to={`/driver/chat/${order.id}`} className="drv-btn drv-btn--ghost drv-btn--sm" style={{ width: 'auto' }}>
            💬 چات
          </Link>
        </div>

        {/* Progress bar */}
        <div className="drv-order__progress">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              className={`drv-order__step ${i < currentIdx ? 'drv-order__step--done' : i === currentIdx ? 'drv-order__step--current' : ''}`}
            />
          ))}
        </div>

        {/* Step labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--drv-text2)', marginTop: -8, marginBottom: 12 }}>
          {STEPS.filter((_, i) => i % 2 === 0).map(s => (
            <span key={s.key}>{s.icon}</span>
          ))}
        </div>
      </div>

      {/* Order info */}
      <div className="drv-card">
        <div className="drv-card__title">تفاصيل الطلب</div>
        <div className="drv-order__info">
          <div className="drv-order__row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/>
            </svg>
            <span>العميل</span>
            <strong>{order.customer?.name}</strong>
          </div>
          <div className="drv-order__row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"/>
            </svg>
            <span>الجوال</span>
            <a href={`tel:${order.customer?.phone}`} style={{ color: 'var(--drv-primary)' }}>
              <strong>{order.customer?.phone}</strong>
            </a>
          </div>
          <div className="drv-order__row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/>
            </svg>
            <span>عنوان التوصيل</span>
            <span style={{ flex: 1, textAlign: 'left', wordBreak: 'break-word' }}>{order.delivery_address || '-'}</span>
          </div>
          <div className="drv-order__row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"/>
            </svg>
            <span>الكمية</span>
            <strong>{order.quantity} طن — {order.water_type}</strong>
          </div>
          {order.customer_notes && (
            <div className="drv-order__row">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"/>
              </svg>
              <span>ملاحظات</span>
              <span style={{ flex: 1, textAlign: 'left' }}>{order.customer_notes}</span>
            </div>
          )}
        </div>

        {/* Map links */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {order.ship?.lat && order.ship?.lng && (
            <button
              className="drv-btn drv-btn--ghost drv-btn--sm"
              onClick={() => openMap(order.ship.lat, order.ship.lng)}
            >
              🗺 موقع الشيب
            </button>
          )}
          {order.delivery_location?.lat && (
            <button
              className="drv-btn drv-btn--ghost drv-btn--sm"
              onClick={() => openMap(order.delivery_location.lat, order.delivery_location.lng)}
            >
              🗺 موقع العميل
            </button>
          )}
        </div>

        {/* Total */}
        <div className="drv-order__total"><Money n={order.discount_amount > 0 ? order.total - order.discount_amount : order.total} /></div>

        {/* Pricing breakdown */}
        {(order.subtotal > 0) && (
          <div style={{ fontSize: 13, color: 'var(--drv-text2)', marginBottom: 16 }}>
            <div className="drv-row"><span>المبلغ الأساسي</span><span><Money n={order.subtotal} /></span></div>
            <div className="drv-row"><span>ضريبة القيمة المضافة</span><span><Money n={order.tax_total} /></span></div>
            <div className="drv-row"><span>رسوم التوصيل</span><span><Money n={order.delivery_fee} /></span></div>
            {order.discount_amount > 0 && (
              <div className="drv-row" style={{ color: '#22c55e' }}>
                <span>خصم كوبون {order.coupon_code && `(${order.coupon_code})`}</span>
                <span>- <Money n={order.discount_amount} /></span>
              </div>
            )}
          </div>
        )}

        {/* Feedback */}
        {error   && <p className="drv-error">{error}</p>}
        {success && <p className="drv-success">{success}</p>}

        {/* Action button */}
        {order.next_status && order.status !== 'completed' && (
          <button
            className={`drv-btn ${order.next_status === 'completed' ? 'drv-btn--green' : 'drv-btn--primary'}`}
            onClick={updateStatus}
            disabled={updating}
            style={{ fontSize: 16, padding: 16 }}
          >
            {updating
              ? <span className="drv-spinner drv-spinner--sm"/>
              : order.next_status === 'completed'
                ? `✅ تأكيد استلام الدفع وإتمام الطلب`
                : `← ${order.next_label || order.next_status}`
            }
          </button>
        )}

        {order.status === 'completed' && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>🎉</div>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>تم إتمام الطلب بنجاح!</div>
            <Link to="/driver/home" className="drv-btn drv-btn--green" style={{ display: 'flex', maxWidth: 200, margin: '0 auto' }}>
              العودة للرئيسية
            </Link>
          </div>
        )}
      </div>

      {/* Ship info */}
      {order.ship?.name && (
        <div className="drv-card">
          <div className="drv-card__title">معلومات الشيب</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>🚢 {order.ship.name}</div>
          {order.ship.address && (
            <div style={{ fontSize: 14, color: 'var(--drv-text2)' }}>{order.ship.address}</div>
          )}
        </div>
      )}
    </div>
  );
}
